import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

const SUB_STAGES = ["PO", "MFC", "DELIVERY"];

export function WeightageEditor({ projectId, category, canEdit, onSaved }) {
  const [items, setItems] = useState([]);
  const [weights, setWeights] = useState({});
  const [subStages, setSubStages] = useState({ PO: 0.2, MFC: 0.1, DELIVERY: 0.7 });
  const [normalize, setNormalize] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: leafItems }, { data: allWeights }] = await Promise.all([
      api.get(`/projects/${projectId}/wbs`, { params: { category, include_groups: false } }),
      api.get(`/projects/${projectId}/weightages`),
    ]);
    setItems(leafItems);
    const w = {};
    allWeights.filter((x) => x.scope === "ITEM").forEach((x) => { w[x.wbs_item_id] = x.weight; });
    setWeights(w);
    if (category === "PROCUREMENT") {
      const sub = {};
      allWeights.filter((x) => x.scope === "SUB_STAGE").forEach((x) => { sub[x.key] = x.weight; });
      if (Object.keys(sub).length) setSubStages({ PO: 0.2, MFC: 0.1, DELIVERY: 0.7, ...sub });
    }
  }, [projectId, category]);

  useEffect(() => { load(); }, [load]);

  const saveItemWeights = async () => {
    setSaving(true);
    try {
      const payload = items.filter((i) => weights[i.id] !== undefined && weights[i.id] !== "").map((i) => ({
        scope: "ITEM", wbs_item_id: i.id, weight: Number(weights[i.id]),
      }));
      if (payload.length === 0) { toast.info("Enter at least one weight first"); return; }
      await api.put(`/projects/${projectId}/weightages`, { weights: payload, normalize });
      toast.success("Item weights saved");
      load();
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveSubStages = async () => {
    try {
      await api.put(`/projects/${projectId}/weightages`, {
        weights: SUB_STAGES.map((s) => ({ scope: "SUB_STAGE", key: s, weight: Number(subStages[s] || 0) })),
      });
      toast.success("Sub-stage split saved");
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`weightage-editor-${category}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Item weights within {category}</h3>
        {canEdit && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5"><Switch checked={normalize} onCheckedChange={setNormalize} data-testid="normalize-weights-switch" /><Label className="text-xs">Normalize to 1.0</Label></div>
            <Button size="sm" disabled={saving} onClick={saveItemWeights} data-testid="save-item-weights-button">Save weights</Button>
          </div>
        )}
      </div>

      {category === "PROCUREMENT" && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border p-3">
          <p className="text-xs text-muted-foreground">Sub-stage split (PO / MFC / Delivery):</p>
          {SUB_STAGES.map((s) => (
            <div key={s}>
              <Label className="text-xs">{s}</Label>
              <Input type="number" step="0.01" disabled={!canEdit} value={subStages[s]} onChange={(e) => setSubStages({ ...subStages, [s]: e.target.value })} data-testid={`sub-stage-input-${s}`} className="mt-1 w-20" />
            </div>
          ))}
          {canEdit && <Button size="sm" variant="outline" onClick={saveSubStages} data-testid="save-sub-stage-button">Save</Button>}
        </div>
      )}

      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="w-32">Weight (0–1)</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((i) => (
            <TableRow key={i.id}>
              <TableCell className="font-mono text-xs">{i.code}</TableCell>
              <TableCell className="text-foreground">{i.name}</TableCell>
              <TableCell>
                <Input
                  type="number" step="0.01" min="0" max="1" disabled={!canEdit}
                  value={weights[i.id] ?? ""}
                  onChange={(e) => setWeights({ ...weights, [i.id]: e.target.value })}
                  data-testid={`wbs-weightage-input-${i.id}`}
                  className="h-8 w-24"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
